/**
 * Sysadmin MCP catalog — list view (Wave 4 PR-F-rebuild).
 *
 * R5 P0-1/P0-2 fix (2026-05-13): split the parent leaf route into a
 * pure layout (`catalog.mcp.tsx`) + this index route. Without the
 * split, navigating to `/sysadmin/catalog/mcp/<id>` mounted the list
 * page because `catalog.mcp.tsx` was a list component without an
 * <Outlet/>, so TanStack had no slot to render the child route in.
 * This file now holds the list-page content under the
 * `/sysadmin/catalog/mcp/` index route ID. Mirrors the canonical
 * pattern used by `routes/sysadmin/models.tsx` (layout) +
 * `routes/sysadmin/models/index.tsx` (list).
 *
 * Click row → `/sysadmin/catalog/mcp/$id` for full detail + assignments.
 */

import { useMemo, useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import {
  Network,
  Plus,
  Search,
  ArrowRight,
  CircleDot,
  Activity as ActivityIcon,
  Users as UsersIcon,
  Server,
  Cloud,
  Globe,
  Shield,
  ShieldCheck,
  ShieldQuestion,
  AlertTriangle,
} from 'lucide-react';
import { buttonVariants } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  useSysadminMcpCatalog,
  MCP_HOSTING_MODES,
  MCP_TRUST_LEVELS,
  type McpHostingMode,
  type McpTrustLevel,
  type SysadminMcpCatalogRow,
} from '@/lib/use-sysadmin-mcp';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/sysadmin/catalog/mcp/')({
  component: McpCatalogListPage,
});

function McpCatalogListPage() {
  const [searchInput, setSearchInput] = useState('');
  const [hostingFilter, setHostingFilter] = useState<McpHostingMode | 'all'>('all');
  const [trustFilter, setTrustFilter] = useState<McpTrustLevel | 'all'>('all');
  const [includeRetired, setIncludeRetired] = useState(false);

  const { data, isLoading, error } = useSysadminMcpCatalog({
    search: searchInput.trim() || undefined,
    hostingMode: hostingFilter !== 'all' ? hostingFilter : undefined,
    trustLevel: trustFilter !== 'all' ? trustFilter : undefined,
    includeRetired,
    limit: 100,
  });

  const stats = useMemo(() => {
    const items = data?.items ?? [];
    const published = items.filter((m) => m.isPublished && !m.retiredAt).length;
    const retired = items.filter((m) => m.retiredAt).length;
    const totalInstalls = items.reduce(
      (sum, m) => sum + (m.activeInstallCount ?? 0),
      0,
    );
    return { total: items.length, published, retired, totalInstalls };
  }, [data]);

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
            <Network className="h-6 w-6" />
            MCP Servers
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            The shared MCP server catalog tenants install. Sysadmin authors here;
            tenants see only the assigned subset in redacted form (per I-M6.7).
          </p>
        </div>
        <Link
          to="/sysadmin/catalog/mcp/new"
          className={buttonVariants()}
        >
          <Plus className="h-4 w-4 mr-2" />
          New MCP server
        </Link>
      </div>

      {/* Stats strip (mirrors models/index.tsx) */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatTile label="Total" value={stats.total} />
        <StatTile label="Published" value={stats.published} hint="visible to tenants" />
        <StatTile label="Retired" value={stats.retired} hint="hidden, no new installs" />
        <StatTile label="Active installs" value={stats.totalInstalls} hint="across all tenants" />
      </div>

      {/* Search + filters */}
      <Card>
        <CardContent className="py-4">
          <div className="flex flex-col md:flex-row gap-3 items-stretch md:items-center">
            <div className="relative flex-1">
              <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search by name, slug, or description…"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select
              value={hostingFilter}
              onValueChange={(v) => setHostingFilter(v as McpHostingMode | 'all')}
            >
              <SelectTrigger className="md:w-44">
                <SelectValue placeholder="Hosting" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All hosting modes</SelectItem>
                {MCP_HOSTING_MODES.map((m) => (
                  <SelectItem key={m} value={m}>
                    {hostingLabel(m)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={trustFilter}
              onValueChange={(v) => setTrustFilter(v as McpTrustLevel | 'all')}
            >
              <SelectTrigger className="md:w-40">
                <SelectValue placeholder="Trust" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All trust levels</SelectItem>
                {MCP_TRUST_LEVELS.map((t) => (
                  <SelectItem key={t} value={t}>
                    {t}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer">
              <input
                type="checkbox"
                checked={includeRetired}
                onChange={(e) => setIncludeRetired(e.target.checked)}
                className="rounded border-input"
              />
              Show retired
            </label>
          </div>
        </CardContent>
      </Card>

      {/* Catalog table */}
      <Card>
        <CardContent className="p-0">
          {error ? (
            <div className="p-8 text-center text-destructive text-sm">
              Failed to load catalog: {error.message}
            </div>
          ) : isLoading ? (
            <div className="p-8 text-center text-muted-foreground text-sm animate-pulse">
              Loading catalog…
            </div>
          ) : (data?.items ?? []).length === 0 ? (
            <EmptyState
              searching={
                !!searchInput.trim() ||
                hostingFilter !== 'all' ||
                trustFilter !== 'all'
              }
            />
          ) : (
            <TooltipProvider>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Server</TableHead>
                    <TableHead>Hosting</TableHead>
                    <TableHead>Trust</TableHead>
                    <TableHead className="text-right">Version</TableHead>
                    <TableHead className="text-right">Installs</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="w-12"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(data?.items ?? []).map((m) => (
                    <McpRow key={m.id} m={m} />
                  ))}
                </TableBody>
              </Table>
            </TooltipProvider>
          )}
        </CardContent>
      </Card>

      {/* Footer summary */}
      {!isLoading && data && (
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <div className="flex items-center gap-4">
            <span className="flex items-center gap-1.5">
              <ActivityIcon className="h-3.5 w-3.5" />
              {stats.published} published
            </span>
            <span className="flex items-center gap-1.5">
              <UsersIcon className="h-3.5 w-3.5" />
              {stats.totalInstalls} active installs
            </span>
          </div>
          {data.nextCursor && (
            <span className="text-amber-600">More rows available — refine search to see them</span>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Row ──────────────────────────────────────────────────────────────

function McpRow({ m }: { m: SysadminMcpCatalogRow }) {
  return (
    <TableRow className="cursor-pointer hover:bg-muted/40">
      <TableCell>
        <Link
          to="/sysadmin/catalog/mcp/$id"
          params={{ id: m.id }}
          className="block"
        >
          <div className="font-medium">{m.displayName}</div>
          <div className="text-xs text-muted-foreground font-mono mt-0.5">
            {m.slug}
          </div>
          <div className="text-xs text-muted-foreground mt-1 line-clamp-1 max-w-md">
            {m.description}
          </div>
        </Link>
      </TableCell>
      <TableCell>
        <HostingBadge mode={m.hostingMode} protocol={m.protocol} />
      </TableCell>
      <TableCell>
        <TrustBadge level={m.trustLevel} />
      </TableCell>
      <TableCell className="text-right tabular-nums">{m.version}</TableCell>
      <TableCell className="text-right tabular-nums">
        {m.activeInstallCount}
      </TableCell>
      <TableCell>
        <LifecycleBadge row={m} />
      </TableCell>
      <TableCell>
        <Tooltip>
          <TooltipTrigger asChild>
            <Link
              to="/sysadmin/catalog/mcp/$id"
              params={{ id: m.id }}
              className="text-muted-foreground hover:text-foreground inline-flex"
              aria-label={`Open ${m.displayName}`}
              title={`Open ${m.displayName}`}
            >
              <ArrowRight className="h-4 w-4" />
            </Link>
          </TooltipTrigger>
          <TooltipContent>Open detail</TooltipContent>
        </Tooltip>
      </TableCell>
    </TableRow>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────

function StatTile({
  label,
  value,
  hint,
}: { label: string; value: number; hint?: string }) {
  return (
    <div className="rounded-md border p-4">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="text-2xl font-semibold tabular-nums">{value}</div>
      {hint && <div className="text-[11px] text-muted-foreground mt-1">{hint}</div>}
    </div>
  );
}

function hostingLabel(mode: McpHostingMode): string {
  switch (mode) {
    case 'platform_hosted': return 'Platform-hosted';
    case 'remote_hosted': return 'Remote-hosted';
    case 'rest_wrapped': return 'REST-wrapped';
  }
}

function HostingBadge({ mode, protocol }: { mode: McpHostingMode; protocol: string }) {
  const Icon = mode === 'platform_hosted' ? Server : mode === 'remote_hosted' ? Cloud : Globe;
  const tone =
    mode === 'platform_hosted'
      ? 'border-emerald-300 text-emerald-700'
      : mode === 'remote_hosted'
        ? 'border-blue-300 text-blue-700'
        : 'border-purple-300 text-purple-700';
  return (
    <div className="flex flex-col gap-1">
      <Badge variant="outline" className={cn('text-[10px] gap-1 w-fit', tone)}>
        <Icon className="h-3 w-3" />
        {hostingLabel(mode)}
      </Badge>
      <span className="text-[10px] text-muted-foreground font-mono">{protocol}</span>
    </div>
  );
}

function TrustBadge({ level }: { level: McpTrustLevel }) {
  const Icon =
    level === 'official'
      ? ShieldCheck
      : level === 'verified'
        ? Shield
        : level === 'community'
          ? ShieldQuestion
          : AlertTriangle;
  const tone =
    level === 'official'
      ? 'border-emerald-300 text-emerald-700'
      : level === 'verified'
        ? 'border-blue-300 text-blue-700'
        : level === 'community'
          ? 'border-amber-300 text-amber-700'
          : 'border-rose-300 text-rose-700';
  return (
    <Badge variant="outline" className={cn('text-[10px] gap-1', tone)}>
      <Icon className="h-3 w-3" />
      {level}
    </Badge>
  );
}

function LifecycleBadge({ row }: { row: SysadminMcpCatalogRow }) {
  if (row.retiredAt) {
    return (
      <Badge variant="outline" className="text-muted-foreground text-[10px]">
        retired
      </Badge>
    );
  }
  if (!row.isPublished) {
    return (
      <Badge variant="outline" className="text-amber-700 border-amber-300 text-[10px]">
        draft
      </Badge>
    );
  }
  return (
    <Badge
      variant="outline"
      className="text-emerald-700 border-emerald-300 gap-1 text-[10px]"
    >
      <CircleDot className="h-3 w-3" />
      published
    </Badge>
  );
}

function EmptyState({ searching }: { searching: boolean }) {
  if (searching) {
    return (
      <div className="p-8 text-center text-sm text-muted-foreground">
        No MCP servers match your filters.
      </div>
    );
  }
  return (
    <div className="p-8 text-center space-y-3">
      <Network className="h-10 w-10 text-muted-foreground mx-auto" />
      <h3 className="text-base font-semibold">No MCP servers yet</h3>
      <p className="text-sm text-muted-foreground max-w-md mx-auto">
        The catalog is empty. Create your first MCP server entry to make it
        installable by tenants.
      </p>
      <Link to="/sysadmin/catalog/mcp/new" className={buttonVariants()}>
        <Plus className="h-4 w-4 mr-2" />
        New MCP server
      </Link>
    </div>
  );
}
